<template>
  <div :class="['submission-detail-page', 'questionnaire-theme', { 'has-resubmit': canResubmit && normalizedFormConfig && normalizedFormConfig.fields && normalizedFormConfig.fields.length }]">
    <van-nav-bar title="提交详情" />
    <van-loading v-if="loading && !detail" class="loading-center" />
    <div v-else-if="loadError" class="load-error">
      <van-empty description="加载失败，请稍后重试" />
    </div>
    <template v-else-if="detail">
      <div class="feedback-section" v-if="hasFeedback">
        <div class="section-title">反馈信息</div>
        <div class="feedback-card">
          <div class="feedback-row">
            <span class="label">状态</span>
            <span class="value">{{ feedbackStatusName }}</span>
          </div>
          <div class="feedback-row" v-if="latestFeedback.comment">
            <span class="label">说明</span>
            <span class="value">{{ latestFeedback.comment }}</span>
          </div>
          <div class="feedback-row" v-if="latestFeedback.feedbackAt">
            <span class="label">反馈时间</span>
            <span class="value">{{ formatDate(latestFeedback.feedbackAt) }}</span>
          </div>
        </div>
      </div>
      <div class="content-section">
        <div class="section-title">提交内容</div>
        <DynamicQuestionnaireForm
          v-if="normalizedFormConfig && normalizedFormConfig.fields && normalizedFormConfig.fields.length"
          ref="dynamicForm"
          :form-config="normalizedFormConfig"
          :value="formValue"
          :readonly="!canResubmit"
        />
        <div v-else class="content-fallback">
          <div v-if="detail.contentSummary" class="content-summary">{{ detail.contentSummary }}</div>
          <div v-else class="content-kv-list">
            <div
              v-for="(val, key) in contentMap"
              :key="key"
              class="content-kv-row"
            >
              <span class="kv-label">{{ fieldLabelByKey(key) }}</span>
              <span class="kv-value">{{ formatContentValue(val, fieldByKey(key)) }}</span>
            </div>
            <van-empty v-if="!Object.keys(contentMap).length" description="暂无提交内容" />
          </div>
        </div>
      </div>
      <div v-if="canResubmit && normalizedFormConfig && normalizedFormConfig.fields && normalizedFormConfig.fields.length" class="footer-actions">
        <van-button
          block
          type="primary"
          round
          class="btn-resubmit"
          :loading="submitting"
          @click="resubmit"
        >
          重新提交
        </van-button>
      </div>
    </template>
  </div>
</template>

<script>
import Vue from 'vue'
import { NavBar, Loading, Empty, Button, Toast } from 'vant'
import { getFormDetail, getSubmissionDetail, submitSubmission } from '@/api/questionnaire'
import DynamicQuestionnaireForm from './components/DynamicQuestionnaireForm'

Vue.use(NavBar).use(Loading).use(Empty).use(Button).use(Toast)

export default {
  name: 'QuestionnaireSubmissionDetail',
  components: { DynamicQuestionnaireForm },
  data() {
    return {
      formId: '',
      submissionId: '',
      detail: null,
      formConfig: null,
      editableContent: {},
      submitting: false,
      loading: false,
      loadError: false
    }
  },
  computed: {
    contentMap() {
      const c = this.detail && this.detail.content
      return (c && typeof c === 'object') ? c : {}
    },
    canResubmit() {
      return this.detail && this.detail.allowResubmitEdit === true
    },
    formValue() {
      return this.canResubmit ? this.editableContent : this.contentMap
    },
    normalizedFormConfig() {
      const fc = this.formConfig
      if (!fc) return null
      if (typeof fc === 'object' && fc !== null && Array.isArray(fc.fields)) {
        return fc
      }
      if (typeof fc === 'string') {
        try {
          const parsed = JSON.parse(fc)
          return (parsed && typeof parsed === 'object' && Array.isArray(parsed.fields)) ? parsed : null
        } catch {
          return null
        }
      }
      return null
    },
    latestFeedback() {
      return this.detail && this.detail.latestFeedback
    },
    feedbackStatusName() {
      const fb = this.latestFeedback
      if (!fb) return '-'
      return fb.statusName || fb.status || '-'
    },
    hasFeedback() {
      const fb = this.latestFeedback
      if (!fb || typeof fb !== 'object') return false
      return !!(fb.status || fb.statusName || fb.comment || fb.feedbackAt)
    }
  },
  watch: {
    '$route.query': {
      handler(query) {
        this.formId = query.formId || ''
        this.submissionId = query.submissionId || ''
        if (this.formId && this.submissionId) {
          this.fetch()
        }
      },
      immediate: true
    }
  },
  methods: {
    fetch() {
      if (!this.formId || !this.submissionId) return
      this.loading = true
      this.loadError = false
      Promise.all([
        getFormDetail(this.formId),
        getSubmissionDetail(this.formId, this.submissionId)
      ])
        .then(([formRes, subRes]) => {
          const formData = formRes && (formRes.data === undefined ? formRes : formRes.data)
          const subData = subRes && (subRes.data === undefined ? subRes : subRes.data)
          const fc = formData && formData.formConfig
          this.formConfig = (fc && typeof fc === 'object') ? fc : null
          this.detail = subData || null
          const c = subData && subData.content
          this.editableContent = (c && typeof c === 'object') ? { ...c } : {}
        })
        .catch(() => {
          this.loadError = true
        })
        .finally(() => {
          this.loading = false
        })
    },
    fieldByKey(key) {
      const fields = this.normalizedFormConfig?.fields || []
      return fields.find(f => f.key === key) || null
    },
    fieldLabelByKey(key) {
      const f = this.fieldByKey(key)
      return f ? f.label : key
    },
    formatDate(val) {
      if (!val) return '-'
      const d = new Date(val)
      return Number.isNaN(d.getTime()) ? val : d.toLocaleString('zh-CN')
    },
    formatContentValue(val, field) {
      if (val == null) return '-'
      if (val && typeof val === 'object' && Array.isArray(val.selected)) {
        const opts = (field && field.options) || []
        const parts = (val.selected || []).map(v => {
          const opt = opts.find(o => String(o.value) === String(v))
          const label = opt ? opt.label : String(v)
          const extra = (val.extraInputMap || {})[v]
          return extra ? `${label}: ${extra}` : label
        })
        return parts.join(', ')
      }
      if (Array.isArray(val)) {
        return val.map(item => (item && typeof item === 'object' && item.url) ? item.url : String(item)).join(', ')
      }
      if (typeof val === 'object') return (val.url || val.label || JSON.stringify(val))
      return String(val)
    },
    getCurrentContent() {
      const ref = this.$refs.dynamicForm
      return (ref && typeof ref.getContent === 'function') ? ref.getContent() : this.editableContent
    },
    validateRequired() {
      if (!this.normalizedFormConfig || !Array.isArray(this.normalizedFormConfig.fields)) return true
      const content = this.getCurrentContent()
      const required = this.normalizedFormConfig.fields.filter(f => f.required)
      for (const f of required) {
        const val = content[f.key]
        if (val == null || val === '') {
          Toast(f.label ? `请填写${f.label}` : '请完善必填项')
          return false
        }
        if (Array.isArray(val) && val.length === 0) {
          Toast(f.label ? `请上传${f.label}` : '请完善必填项')
          return false
        }
        if (f.type === 'choice' && (f.mode === 'single' || f.mode === 'multi')) {
          const selected = val.selected || []
          if (!selected.length) {
            Toast(f.label ? `请选择${f.label}` : '请完善必填项')
            return false
          }
          const opts = f.options || []
          for (const opt of opts) {
            if (opt.allowInput && opt.inputRequired && selected.includes(opt.value)) {
              const extra = (val.extraInputMap || {})[opt.value]
              if (!extra || String(extra).trim() === '') {
                Toast(opt.label ? `请填写${opt.label}的补充内容` : '请完善必填项')
                return false
              }
            }
          }
        }
      }
      return true
    },
    resubmit() {
      if (!this.validateRequired()) return
      this.submitting = true
      submitSubmission(this.formId, {
        submissionId: this.submissionId,
        submitStatus: 'SUBMITTED',
        content: this.getCurrentContent()
      })
        .then(() => {
          Toast('提交成功')
          this.$router.replace({
            path: '/questionnaire/detail',
            query: { formId: this.formId, tab: 'myList' }
          }).catch(() => {})
        })
        .catch(() => {
          Toast('提交失败')
        })
        .finally(() => {
          this.submitting = false
        })
    }
  }
}
</script>

<style lang="scss" scoped>
.submission-detail-page {
  min-height: 100vh;
  padding-bottom: 24px;
  background: #f5f5f5;

  &.has-resubmit {
    padding-bottom: 100px;
  }
}

.loading-center {
  display: flex;
  justify-content: center;
  padding: 40px 0;
}

.load-error {
  padding: 24px;
}

.feedback-section {
  padding: 16px;
  background: #fff;
  margin-bottom: 12px;

  .section-title {
    font-size: 14px;
    font-weight: 600;
    color: #323233;
    margin-bottom: 12px;
  }

  .feedback-card {
    background: #f7f8fa;
    padding: 12px 16px;
    border-radius: 4px;

    .feedback-row {
      display: flex;
      margin-bottom: 8px;
      font-size: 14px;

      &:last-child {
        margin-bottom: 0;
      }

      .label {
        color: #646566;
        min-width: 72px;
      }

      .value {
        color: #323233;
        flex: 1;
      }
    }
  }
}

.content-section {
  background: #fff;
  padding: 0 0 16px;

  .section-title {
    font-size: 14px;
    font-weight: 600;
    color: #323233;
    padding: 16px 16px 8px;
  }
}

.content-fallback {
  padding: 0 16px 16px;

  .content-summary {
    font-size: 14px;
    line-height: 1.6;
    color: #323233;
    padding: 12px 0;
  }

  .content-kv-list {
    .content-kv-row {
      display: flex;
      padding: 10px 0;
      font-size: 14px;
      border-bottom: 1px solid #ebedf0;

      &:last-child {
        border-bottom: none;
      }

      .kv-label {
        min-width: 80px;
        color: #646566;
      }

      .kv-value {
        flex: 1;
        color: #323233;
        word-break: break-all;
      }
    }
  }
}

.footer-actions {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 12px 16px;
  background: #fff;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06);

  .btn-resubmit {
    height: 44px;
    font-size: 16px;
    background: var(--qn-theme);
    border: none;
  }
}
</style>
